// Spalten-Definitionen fuers Dashboard. Render-Funktionen werden im
// DashboardTable importiert. Hier nur Metadaten, damit Tests einfach
// laufen (ohne JSX).

export type ColumnDef = {
  key: string;
  label: string;
  defaultActive: boolean;
  align?: 'left' | 'right' | 'center';
  /**
   * Feste Breite in CSS-Pixeln. `null` heisst: diese Spalte bekommt den
   * Rest.
   *
   * WARUM PIXEL UND NICHT TAILWIND-KLASSEN (A10.1)
   *   Vorher standen hier Klassen wie `w-28`. Die Tabelle lief in
   *   automatischem Layout, und dort sind Breitenangaben fuer den Browser
   *   nur VORSCHLAEGE. Gemessen im laufenden Build bei 1024 px: die
   *   Nr.-Spalte bekam 63 px statt der angesetzten 112 — jede einzelne
   *   der 30 Bestellnummern brach um, "B-2026-0042" ueber drei Zeilen.
   *   Eine andere Zahl an `w-28` haette daran nichts geaendert.
   *
   *   Mit `table-fixed` und einem <colgroup> gelten die Zahlen wirklich.
   *   Dafuer muessen sie als Zahl vorliegen und nicht als Klassenname —
   *   die Rest-Spalte rechnet sich aus der Summe der uebrigen.
   */
  breite: number | null;
  /**
   * Darf der Inhalt umbrechen? Bestellnummern nie: eine Nummer ueber drei
   * Zeilen ist keine Nummer mehr, sondern ein Absatz.
   */
  nowrap?: boolean;
  /**
   * Kopfzelle mit enger Polsterung (`px-1` statt `px-3`, also 8 statt 24 px
   * insgesamt). Nur fuer schmale Spalten, deren Zelle ein Symbol traegt und
   * deren Kopftext sonst nicht hineinpasst (A12.12).
   *
   * Nur bei mittig gesetzten Spalten verwenden: dort bleibt die Mitte die
   * Mitte, gleich wie breit gepolstert wird. Bei links- oder rechtsbuendigen
   * Spalten stuende der Kopf um 16 px versetzt zum Zelleninhalt.
   */
  kopfEng?: boolean;
  /** Erklaerung am Spaltenkopf. Nur wo die Beschriftung allein zu knapp ist. */
  tooltip?: string;
};

/** Polsterung der Kopfzelle in px, beide Seiten zusammen. `px-3` bzw. `px-1`. */
export const KOPF_POLSTERUNG_WEIT = 24;
export const KOPF_POLSTERUNG_ENG = 8;

export function kopfPolsterung(c: ColumnDef): number {
  return c.kopfEng ? KOPF_POLSTERUNG_ENG : KOPF_POLSTERUNG_WEIT;
}

// Die Breiten sind am laufenden Build nachgemessen, nicht geschaetzt.
//
// DIE REGEL (A12.12): Eine feste Spaltenbreite bemisst sich am BREITEREN
// von beidem — Zelleninhalt oder KOPFTEXT —, jeweils plus Polsterung.
//
// Der Kopftext war bis A12.12 nie geprueft. Der Kommentar sagte "so gross
// wie ihr breitester Inhalt plus Polsterung" und meinte damit nur den
// Zelleninhalt. Bei 'quelle' und 'versandart' ist der Inhalt ein Symbol,
// wofuer 48 px reichlich sind — die Koepfe standen deshalb monatelang als
// "Qu…" und "Lie…" da. Gemessen bei 14 px Poppins: "Quelle" 45 px,
// "Lieferung" 65 px, bei 24 px Innenraum.
//
// `truncate` bleibt als Netz stehen, darf aber im Standard-Spaltensatz
// nicht greifen. dashboardSpalten.test.ts prueft genau dieses Verhaeltnis
// fuer jede Spalte — auch fuer die abschaltbaren, denn zugeschaltet sollen
// sie ebenso lesbar sein.
//
// Wer eine Breite oder eine Beschriftung aendert, misst bitte neu.
export const COLUMNS: readonly ColumnDef[] = [
  // Traegt bei Shop-Bestellungen zwei Nummern uebereinander (G-Nummer und
  // darunter klein die eigene). Beide duerfen nicht umbrechen, deshalb
  // richtet sich die Breite nach der laengeren Form "B-2026-0042".
  { key: 'bestellnr',  label: 'Nr.',       defaultActive: true,  breite: 112, nowrap: true },
  // 56 und 76 statt zweimal 48, dazu enge Kopf-Polsterung (A12.12). Beide
  // Zellen tragen nur ein Symbol; masgebend ist hier der Kopftext. Mit
  // `px-1` bleiben 48 bzw. 68 px Innenraum fuer 45 bzw. 65 px Text.
  //
  // Warum "Lieferung" stehen bleibt und nicht zu "Versand" gekuerzt wurde:
  // die Spalte sagt Abholung ODER Versand: "Versand" waere fuer den halben
  // Bestand schlicht falsch. Die Verbreiterung kostet den Titel zusammen
  // 36 px und bleibt damit unter der Grenze, ab der Kuerzen guenstiger waere.
  { key: 'quelle',     label: 'Quelle',    defaultActive: true,  breite: 56,  align: 'center', kopfEng: true },
  { key: 'versandart', label: 'Lieferung', defaultActive: true,  breite: 76,  align: 'center', kopfEng: true },
  // OBERGRENZE, kein Wunsch (A10.1). Vorher hatte diese Spalte gar keine
  // Angabe und nahm sich 466 von 1049 Pixeln — 44 Prozent der Breite fuer
  // einen Namen. Was nicht hineinpasst, wird abgeschnitten; der volle Name
  // steht in der Bestellung.
  { key: 'kunde',      label: 'Kunde',     defaultActive: true,  breite: 148 },
  // DIE REST-SPALTE. Titel sind unterschiedlich lang und das einzige
  // Feld, dem zusaetzlicher Platz wirklich nuetzt.
  //
  // Bis A10.1 gab es sie nicht: der Titel stand als zweite Zeile in der
  // Kunden-Zelle. Das machte JEDE Zeile zweizeilig (gemessen 78 bis 108 px
  // statt gut 40) und war damit der groesste Posten dabei, dass auf einen
  // Schirm nur sieben Bestellungen passten.
  { key: 'titel',      label: 'Titel',     defaultActive: true,  breite: null },
  // 196 und nicht 144 (nachgemessen NACH A11): seit die Pille die
  // Versandart traegt, traegt der Zustand das laengere Wort — damals
  // "Versandfertig", heute "Abholbereit" (A46).
  // Zusammen mit dem Merkzeichen "12 offen" braucht die Zelle 172 px;
  // bei 120 nutzbaren wurde sie abgeschnitten — mitten im Wort, und
  // ausgerechnet in der Spalte, die sagt, wie weit die Bestellung ist.
  //
  // Umbrechen darf sie nicht (das hob frueher jede Zeile mit an), also
  // bleibt nur breiter. Der Platz geht dem Titel ab; bei 1280 hat der
  // immer noch 264 px.
  //
  // 208 seit A12.7: die Pille traegt jetzt ein Winkelzeichen, weil sie
  // ein Auswahlmenue oeffnet. Nachgemessen im laufenden Build wuchs der
  // breiteste Zelleninhalt von 172 auf 184 px — bei 172 nutzbaren waere
  // ausgerechnet das Zeichen abgeschnitten worden, das sagt, dass hier
  // etwas zu waehlen ist.
  { key: 'zustand',    label: 'Zustand',   defaultActive: true,  breite: 212 },
  // "Termin" statt "Abholung": die Spalte traegt den zugesagten Tag,
  // gleich ob die Ware abgeholt oder versendet wird.
  {
    key: 'abholdatum',
    label: 'Termin',
    defaultActive: true,
    breite: 92,
    tooltip: 'Zugesagter Abhol- bzw. Liefertermin'
  },
  // 140 statt 92 (A45.2): Hier wurde die Regel aus A12.12 —
  // max(Zelleninhalt, Kopftext) + Polster — beim Anlegen nicht
  // angewandt. „vor 3 Tagen" passte nie hinein; auf dem Bild des
  // Betreibers stand „vor 3 Tag…".
  //
  // Nachgemessen im laufenden Bau, in der wirklichen Schrift
  // (Poppins 300, 14px/20px) und an einer echten Zelle als Vorlage.
  // Die Zelle traegt einen Punkt (6 px) und einen Abstand (8 px) vor
  // dem Text, das Polster der Spalte ist 12 + 12:
  //     Text            nur Text   + Punkt/Abstand   + Polster
  //     „heute"            40,1        54,1             78,1
  //     „gestern"          52,9        66,9             90,9
  //     „vor 3 Tagen"      81,3        95,3            119,3
  //     „vor 12 Tagen"     85,2        99,2            123,2
  //     „vor 888 Tagen"    99,3       113,3            137,3
  //     „vor 8888 Tagen"  108,1       122,1            146,1
  //     Kopf „Erfasst"     46,2          —              70,2
  //
  // 140 deckt alles bis zu dreistelligen Tageszahlen; „Gleichstand ist
  // kein Sitz" (A12.12), deshalb 140 und nicht 138.
  //
  // VIERSTELLIG WIRD ABGESCHNITTEN, und das ist eine Entscheidung, keine
  // Nachlaessigkeit: „vor 1000 Tagen" braucht 148 und kaeme fruehestens
  // nach knapp drei Jahren vor, ausschliesslich im Archiv. Die 8 Pixel
  // gingen dem Titel ab, der bei 1024 ohnehin die engste Spalte ist —
  // und das genaue Datum steht in der Sprechblase der Zelle.
  { key: 'erfasst',    label: 'Erfasst',   defaultActive: true,  breite: 140 },
  { key: 'erfasser',   label: 'Wer',       defaultActive: true,  breite: 56,  align: 'center' },
  // 56 statt 52 (A12.12): "Pos." misst 28 px, bei 52 px blieben exakt 28 px
  // Innenraum. Gleichstand ist kein Sitz — ein halbes Pixel Rundung, und
  // `truncate` greift. Rechtsbuendig, deshalb KEIN `kopfEng`: der Kopf
  // stuende sonst versetzt ueber den Zahlen.
  { key: 'anzahl',     label: 'Pos.',      defaultActive: false, breite: 56,  align: 'right' },
  { key: 'preis',      label: 'Summe',     defaultActive: false, breite: 92,  align: 'right' },
  { key: 'verlag',     label: 'Verlag',    defaultActive: false, breite: 140 }
] as const;

/**
 * Mindestbreite der Rest-Spalte. Darunter wird die Tabelle waagerecht
 * gerollt, statt den Titel auf zwei Zeichen zu quetschen.
 */
export const TITEL_MIN_BREITE = 120;

/**
 * Summe der festen Breiten der sichtbaren Spalten — die Zahl, die der
 * Rest-Spalte fehlt.
 */
export function festeBreiteSumme(sichtbar: readonly ColumnDef[]): number {
  return sichtbar.reduce((n, c) => n + (c.breite ?? 0), 0);
}

export const DEFAULT_VISIBLE_COLUMNS: string[] = COLUMNS.filter((c) => c.defaultActive).map(
  (c) => c.key
);

export function formatPreisCent(cent: number | null): string {
  if (cent === null) return '—';
  return (cent / 100).toLocaleString('de-DE', { style: 'currency', currency: 'EUR' });
}
